/**
 * The start view's filter and its options in logical order (Option C, C1, plan §3.2 / §3.6). Pure: no DOM, no React.
 *
 * An engine matches when every whitespace-separated token of the filter occurs in its label, id or keywords (case
 * insensitive), as in the prototype. The entry points are always listed. The logical order — the order Up / Down
 * follow — is Databases, then Azure services, then Other ways to add, each in catalog order; it never depends on how
 * many columns the grid happens to show.
 */
import type { UserFacingEngineId } from '@/core/engines';
import { offeredEngines, type EngineCatalogEntry } from '../state/engineCatalog';
import { ENTRY_POINTS, type EntryPointId } from './entryPoints';

export type StartOptionId = `engine:${UserFacingEngineId}` | `entry:${EntryPointId}`;

export type FilteredEngines = {
  databases: readonly EngineCatalogEntry[];
  azure: readonly EngineCatalogEntry[];
  /** At least one engine matches. */
  any: boolean;
};

function tokensOf(query: string): string[] {
  return query.trim().toLowerCase().split(/\s+/).filter(Boolean);
}

export function engineMatches(entry: EngineCatalogEntry, query: string): boolean {
  const haystack = `${entry.label} ${entry.id} ${entry.keywords}`.toLowerCase();
  return tokensOf(query).every((token) => haystack.includes(token));
}

export function filterEngines(query: string): FilteredEngines {
  const matching = offeredEngines().filter((entry) => engineMatches(entry, query));
  return {
    databases: matching.filter((entry) => entry.group === 'databases'),
    azure: matching.filter((entry) => entry.group === 'azure'),
    any: matching.length > 0,
  };
}

export const engineOptionId = (engine: UserFacingEngineId): StartOptionId => `engine:${engine}`;
export const entryOptionId = (entry: EntryPointId): StartOptionId => `entry:${entry}`;

/** Every visible option in logical order. */
export function startOptionOrder(query: string): StartOptionId[] {
  const { databases, azure } = filterEngines(query);
  return [...databases, ...azure].map((entry) => engineOptionId(entry.id)).concat(ENTRY_POINTS.map((entry) => entryOptionId(entry.id)));
}

/** The first matching engine, the active option while the user types (none when nothing matches). */
export function firstEngineOption(query: string): StartOptionId | null {
  const { databases, azure } = filterEngines(query);
  const first = databases[0] ?? azure[0];
  return first ? engineOptionId(first.id) : null;
}

export function parseStartOption(id: StartOptionId): { kind: 'engine'; engine: UserFacingEngineId } | { kind: 'entry'; entry: EntryPointId } {
  const [kind, value] = id.split(':') as ['engine' | 'entry', string];
  return kind === 'engine' ? { kind, engine: value as UserFacingEngineId } : { kind, entry: value as EntryPointId };
}

/** The polite announcement for a filter (debounced by the caller). */
export function filterAnnouncement(query: string): string {
  const trimmed = query.trim();
  const { databases, azure } = filterEngines(query);
  const count = databases.length + azure.length;
  if (count === 0) return noMatchMessage(trimmed);
  return `${count} ${count === 1 ? 'engine' : 'engines'}`;
}

export function noMatchMessage(trimmedQuery: string): string {
  return `No engine matches “${trimmedQuery}”. Try Paste connection string or Browse Azure.`;
}
